import {match, Pattern} from 'ts-pattern';

import type {
  CheckoutProfileDocumentCell,
  CheckoutProfileDocumentType,
  CheckoutProfileWrite,
  SaleRegime,
} from '../types';

export function getCheckoutProfileDocumentTypes(profile: CheckoutProfileWrite) {
  const types: CheckoutProfileDocumentType[] = ['INVOICE'];

  if (profile.documentTypes?.correctiveInvoice) {
    types.push('CORRECTIVE_INVOICE');
  }

  if (profile.typeOfSale === 'INTERNAL') {
    return types;
  }

  // Tax documents are issued for received deposits only, so they follow the deposit setup.
  if (profile.deposit?.isAllowed) {
    types.push('TAX_DOCUMENT');

    if (profile.documentTypes?.correctiveTaxDocument) {
      types.push('CORRECTIVE_TAX_DOCUMENT');
    }

    types.push('DEPOSIT_INVOICE');
  }

  return types;
}

export function getCheckoutProfileDocumentCells(profile: CheckoutProfileWrite) {
  const types = getCheckoutProfileDocumentTypes(profile);

  if (profile.typeOfSale === 'INTERNAL') {
    return types.map(
      (documentType): CheckoutProfileDocumentCell => ({
        saleRegime: null,
        paymentMethod: null,
        documentType,
      })
    );
  }

  const regimes: SaleRegime[] = ['DOMESTIC'];

  if (profile.saleRegimes?.inEurope) {
    regimes.push('IN_EUROPE');
  }

  if (profile.saleRegimes?.outOfEurope) {
    regimes.push('OUT_OF_EUROPE');
  }

  const balanceMethods = (profile.payments?.methods ?? []).map(({method}) => method);
  const depositMethods = profile.deposit?.isAllowed ? (profile.deposit.methods ?? []) : [];

  return regimes.flatMap((saleRegime) =>
    types.flatMap((documentType) => {
      const methods = match(documentType)
        .with(
          Pattern.union('DEPOSIT_INVOICE', 'TAX_DOCUMENT', 'CORRECTIVE_TAX_DOCUMENT'),
          () => depositMethods
        )
        .otherwise(() => balanceMethods);

      return methods.map(
        (method): CheckoutProfileDocumentCell => ({
          saleRegime,
          paymentMethod: method,
          documentType,
        })
      );
    })
  );
}

export const getCheckoutProfileDocumentCellKey = (cell: CheckoutProfileDocumentCell) =>
  `${cell.saleRegime}|${cell.documentType}|${cell.paymentMethod}`;
